/**
 * @sazeo/sdk — Feature flag hooks
 *
 * Fetches resolved feature flags for the authenticated user via the
 * /functions/v1/flags Edge Function. Results are cached for 5 minutes
 * (stale while re-fetching) to avoid hammering the function on every
 * component mount.
 *
 * Usage:
 *   const enabled = useFlag('home.widget_grid_v2');
 *   if (enabled) return <WidgetGridV2 />;
 *   return <WidgetGridV1 />;
 */

import { useQuery } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// Inlined from @sazeo/shared/flags to avoid circular workspace dep.
// Keep in sync with packages/shared/src/flags.ts.
type FeatureFlagKey =
  | 'home.widget_grid_v2'
  | 'messages.thread_view'
  | 'ai.claude_inline_actions'
  | 'billing.annual_discount'
  | 'people.bulk_import'
  | 'analytics.cohorts.compare'
  | 'signatures.yousign';

// Keep in sync with platform_feature_flags seed in migration 0022.
const FEATURE_FLAG_DEFAULTS: Record<FeatureFlagKey, boolean> = {
  'home.widget_grid_v2': false,
  'messages.thread_view': false,
  'ai.claude_inline_actions': false,
  'billing.annual_discount': false,
  'people.bulk_import': false,
  'analytics.cohorts.compare': false,
  'signatures.yousign': false,
};

export type { FeatureFlagKey };

const FLAGS_STALE_MS = 5 * 60 * 1000; // 5 min

// ---------------------------------------------------------------------------
// Query key
// ---------------------------------------------------------------------------

export const flagsKeys = {
  all: ['feature-flags'] as const,
  resolved: () => [...flagsKeys.all, 'resolved'] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal fetch
// ---------------------------------------------------------------------------

async function fetchFlags(supabase: SupabaseClientLike): Promise<Record<string, boolean>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) return { ...FEATURE_FLAG_DEFAULTS };

  const res = await fetch(buildFunctionUrl('flags'), {
    headers: { authorization: `Bearer ${session.access_token}` },
  });
  if (!res.ok) return { ...FEATURE_FLAG_DEFAULTS };

  const json = (await res.json()) as { flags?: Record<string, boolean> };
  return json.flags ?? { ...FEATURE_FLAG_DEFAULTS };
}

// ---------------------------------------------------------------------------
// useFlags — all resolved flags as a map
// ---------------------------------------------------------------------------

export function useFlags(supabase: SupabaseClientLike): Record<FeatureFlagKey, boolean> {
  const { data } = useQuery({
    queryKey: flagsKeys.resolved(),
    queryFn: () => fetchFlags(supabase),
    staleTime: FLAGS_STALE_MS,
    refetchOnWindowFocus: false,
  });

  return (data ?? { ...FEATURE_FLAG_DEFAULTS }) as Record<FeatureFlagKey, boolean>;
}

// ---------------------------------------------------------------------------
// useFlag — single flag, falls back to compile-time default
// ---------------------------------------------------------------------------

export function useFlag(supabase: SupabaseClientLike, key: FeatureFlagKey): boolean {
  const flags = useFlags(supabase);
  return flags[key] ?? FEATURE_FLAG_DEFAULTS[key] ?? false;
}
